// =============================================================================
// ui/tabs/CoachProveIt.tsx — step 3, one verdict per running rule.
// -----------------------------------------------------------------------------
// ── 🚨 ADHERENCE AND OUTCOME ARE SHOWN SEPARATELY BECAUSE THEY ARE JUDGED
//    SEPARATELY ──
//
// The verdict badge is the engine's answer; the two lines under the tiles are
// the evidence, and they must stay legible as two facts. "That rule didn't work"
// when the truth is "I never followed it" is the single most common false
// conclusion in a trading journal, and a layout that folds the adherence line
// into the outcome re-creates it visually after the engine went to the trouble
// of keeping them apart.
//
// ── 🚨 THE GUARDRAIL IS ALWAYS DRAWN, EVEN WHEN THE LADDER STOPPED EARLIER ──
//
// A falling expectancy matters beside a "not followed" too. It is the trader's
// own P&L and it does not stop being true because the rule went untested.
//
// ── 🚨 BANK IT IS DISABLED UNLESS THE VERDICT IS `worked` ──
//
// Banking is the claim "this is now a habit I have proven". Every other verdict
// is either an absence of evidence or evidence against, and a Bank button that
// works on those turns the ledger into a list of things the trader hoped were
// true. The tooltip says why rather than leaving a dead control.
//
// ── ⚠ MARKING IS ONE WRITE PER CLICK, AND THE ROW REPAINTS FROM THE RESULT ──
//
// Not from an optimistic guess. A mark that failed to save must not sit there
// looking green — the verdict above it is computed from these marks, so a lie
// here propagates into a claim about whether a rule worked.
// =============================================================================

import { useMemo, useState } from 'react';
import { EduMark } from '../components/EduMark';
import { money, num } from '../format';
import { leakPair } from './CoachFixIt';
import {
  ADHERENCE_MIN_MARKED, adherenceMark, leakById, nextMark, proveRule,
  realizedExpectancy,
  type LeakContext, type LeakRule, type LeakTrade, type ProveResult,
  type Verdict,
} from '../../selectors/leaks';

/** A trade as the review list needs it. */
export interface MarkTrade {
  id: string;
  date: string;
  contract: string | null;
  netPnl: number | null;
  ruleAdherences?: Record<string, unknown> | null;
  ruleAdherence?: { ruleId?: unknown; followed?: unknown } | null;
}

/**
 * ⚠ THE NEUTRAL TONE IS AN EMPTY STRING, not `'idle'`. `.coach-badge` already
 * renders the neutral look, so an `.idle` modifier would need a rule that
 * duplicates the base — and `check-styles` finds a class with no rule while
 * NOTHING finds a rule with no class. A modifier that changes nothing is dead
 * either way; better to emit no class at all.
 */
const VERDICTS: Record<Verdict, { label: string; tone: string }> = {
  worked: { label: 'Worked', tone: 'good' },
  'didnt-work': { label: 'Didn’t work', tone: 'warn' },
  'not-followed': { label: 'Not followed', tone: 'bad' },
  guardrail: { label: 'Guardrail', tone: 'bad' },
  'too-soon': { label: 'Too soon', tone: '' },
  unmeasured: { label: 'Unmeasured', tone: '' },
  'no-baseline': { label: 'No baseline', tone: '' },
};

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export function ProveIt(
  { rules, trades, ctx, today, busy, onMark, onBank, onRetire, onSharpen }: {
    rules: readonly LeakRule[];
    /** ⚠ TRADER-WIDE and reaching back to the OLDEST rule. See `readSince`. */
    trades: readonly (LeakTrade & MarkTrade)[];
    ctx: LeakContext;
    today: string;
    busy: boolean;
    onMark: (tradeId: string, ruleId: string, followed: boolean | null) => void;
    onBank: (ruleId: string) => void;
    onRetire: (ruleId: string) => void;
    onSharpen: (leakId: string, prefill: string) => void;
  },
) {
  return (
    <section className="card" data-testid="prove-it">
      <div className="coach-step-head">
        <span className="coach-step-n" aria-hidden="true">3</span>
        <div>
          <div className="coach-step-t">Prove it<EduMark eduKey="coach.prove" /></div>
          <p className="muted">
            {rules.length === 1
              ? 'Did the rule move the number?'
              : 'Did each rule move its number?'}
          </p>
        </div>
      </div>

      {rules.length ? rules.map((r) => (
        <RuleVerdict
          key={r.id}
          rule={r}
          trades={trades}
          ctx={ctx}
          today={today}
          busy={busy}
          onMark={onMark}
          onBank={onBank}
          onRetire={onRetire}
          onSharpen={onSharpen}
        />
      )) : (
        <p className="muted coach-prove-empty">
          No rule active yet. Once one is live its verdict lands here: your frozen
          baseline against now, whether you actually followed it, and whether your
          edge held while you did.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// One rule
// ---------------------------------------------------------------------------

function RuleVerdict(
  { rule, trades, ctx, today, busy, onMark, onBank, onRetire, onSharpen }: {
    rule: LeakRule;
    trades: readonly (LeakTrade & MarkTrade)[];
    ctx: LeakContext;
    today: string;
    busy: boolean;
    onMark: (tradeId: string, ruleId: string, followed: boolean | null) => void;
    onBank: (ruleId: string) => void;
    onRetire: (ruleId: string) => void;
    onSharpen: (leakId: string, prefill: string) => void;
  },
) {
  const [confirm, setConfirm] = useState<'bank' | 'retire' | null>(null);
  const leak = leakById(rule.leakId);

  // ⚠ THE WINDOW IS THE RULE'S OWN: the day it was written until today. It does
  // NOT follow the report's 7d/30d picker — a rule proved over a window the
  // trader can move is not proved at all.
  const since = useMemo(
    () => trades.filter((t) => t.date && t.date >= rule.createdAt && t.date <= today),
    [trades, rule.createdAt, today],
  );

  const v: ProveResult | null = useMemo(() => {
    const exp = realizedExpectancy(since);
    return proveRule(rule, since, ctx, exp.value);
  }, [rule, since, ctx]);

  if (!v || !leak) return null;

  const badge = VERDICTS[v.verdict];
  const a = v.adherence;
  const g = v.guardrail;
  const canBank = v.verdict === 'worked';

  const changeText = v.deltaPct === null
    ? (v.improved === null ? '—' : v.improved ? 'better' : 'no gain')
    : `${v.deltaPct >= 0 ? '+' : ''}${num(v.deltaPct, 0)}%`;
  // ⚠ The tone follows `improved`, not the sign of the raw delta. A tilt gap
  // falling and a capture rate rising are both good, and they move opposite ways.
  const changeTone = v.improved === null ? '' : v.improved ? ' good' : ' bad';
  const shown = leakPair(v.baseline, v.current, v.unit);

  return (
    <div className="coach-verdict" data-rule={rule.id} data-verdict={v.verdict}>
      <div className="coach-verdict-head">
        <div className="coach-verdict-leak">{leak.label}</div>
        <span className={`coach-badge${badge.tone ? ` ${badge.tone}` : ''}`}>{badge.label}</span>
        <EduMark eduKey="coach.verdict-ladder" />
      </div>

      <p className="coach-rule-text">{rule.ruleText}</p>
      <div className="muted coach-rule-meta">
        Active since {rule.createdAt || 'an earlier version'} · measured through {today}
      </div>

      <div className="coach-kpis">
        {/* ⚠ PRINTED AS A PAIR. Rounded separately these two read "−2m" and
            "−2m" beside a change of −24% — see `leakPair`. */}
        <Kpi label="Baseline" value={shown[0]} />
        <Kpi label="Now" value={shown[1]} tone={changeTone} />
        <Kpi label="Change" value={changeText} tone={changeTone} />
        <Kpi label="Sample" value={num(v.n, 0)} />
      </div>

      <p className="coach-adherence">
        {a.pct === null ? (
          <span className="muted">
            No trades marked for this rule yet — adherence can’t be judged, so the
            outcome can’t be trusted either.
          </span>
        ) : (
          <>Held on <b>{num(a.pct, 0)}%</b> of <b>{num(a.marked, 0)}</b> marked
            trade{a.marked === 1 ? '' : 's'} ({num(a.followed, 0)} followed).</>
        )}
        {/* ⚠ THE SECOND MARK FOR THIS KEY ON ONE CARD, and beta has both. They
            are two feet apart — this sentence is the RESULT, the one on the
            review list below is the CONTROL — and a reader who meets either
            first has the same question about it. */}
        <EduMark eduKey="coach.adherence" />
      </p>

      {/* ⚠ ALWAYS DRAWN. See the header — a falling expectancy is worth seeing
          beside a "not followed" too. */}
      <div className="coach-guard">
        <span className="muted coach-k">Guardrail · expectancy per trade</span>
        <EduMark eduKey="coach.guardrail" />
        <div className="coach-guard-v">
          {g.baseline === null ? (
            <span className="muted">
              No expectancy baseline was captured — this rule can’t be certified.
            </span>
          ) : (
            <>
              <b className="mono">{money(g.baseline)}</b>
              {' → '}
              <b className={`mono${g.degraded ? ' bad' : ' good'}`}>
                {money(g.current)}
              </b>
              {' per trade'}
              {g.degraded ? <> — <b className="bad">expectancy degraded</b></> : null}
            </>
          )}
        </div>
      </div>

      <MarkList
        ruleId={rule.id}
        trades={since}
        marked={a.marked}
        busy={busy}
        onMark={onMark}
      />

      <p className="coach-reason">{v.reason}</p>

      <div className="row gap-6 coach-verdict-acts">
        {confirm === null ? (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={!canBank || busy}
              title={canBank
                ? 'Bank this as a proven habit'
                : 'Only a rule with a Worked verdict can be banked'}
              onClick={() => setConfirm('bank')}
            >✓ Bank it</button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => onSharpen(rule.leakId, rule.ruleText)}
            >✎ Sharpen it</button>
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              onClick={() => setConfirm('retire')}
            >✕ Retire</button>
          </>
        ) : (
          // ⚠ A SECOND PRESS, not a browser confirm. Both actions move the rule
          // out of the active list, and the sentence names what happens to the
          // others — the commonest fear here is that closing one stops them all.
          <>
            <span className="coach-confirm">
              {confirm === 'bank'
                ? 'Bank this rule as a proven habit? It moves into your ledger; your other rules keep running.'
                : 'Retire this rule without banking it? It stays in your ledger as a hypothesis you tested.'}
            </span>
            <button
              type="button"
              className={`btn ${confirm === 'bank' ? 'primary' : 'danger'}`}
              disabled={busy}
              onClick={() => {
                setConfirm(null);
                if (confirm === 'bank') onBank(rule.id); else onRetire(rule.id);
              }}
            >{busy ? 'Saving…' : confirm === 'bank' ? 'Yes, bank it' : 'Yes, retire it'}</button>
            <button type="button" className="btn" disabled={busy}
              onClick={() => setConfirm(null)}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi(
  { label, value, tone }: { label: string; value: string; tone?: string | undefined },
) {
  return (
    <div className="coach-kpi">
      <div className="muted coach-k">{label}</div>
      <div className={`mono coach-kpi-v${tone ?? ''}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The adherence review list
// ---------------------------------------------------------------------------

/**
 * The place a rule is actually judged, trade by trade.
 *
 * ⚠ COLLAPSED BY DEFAULT. Several rule cards stack, and an open list of every
 * trade since each rule started buries the verdicts under it.
 *
 * ⚠ NEWEST FIRST — the trades a trader remembers.
 */
function MarkList(
  { ruleId, trades, marked, busy, onMark }: {
    ruleId: string;
    trades: readonly MarkTrade[];
    marked: number;
    busy: boolean;
    onMark: (tradeId: string, ruleId: string, followed: boolean | null) => void;
  },
) {
  const sorted = useMemo(
    () => [...trades].sort((a, b) => b.date.localeCompare(a.date)),
    [trades],
  );

  // ⚠ NO EMPTY STATE, deliberately. With no trades in the window the adherence
  // line above already says nothing is marked and the verdict reason says how
  // many are needed. A third message saying the same thing is noise.
  if (!sorted.length) return null;

  return (
    <details className="coach-marks">
      <summary>
        Mark adherence — <span className="coach-mkcount">
          {num(marked, 0)}/{num(sorted.length, 0)}
        </span> marked<EduMark eduKey="coach.adherence" />
      </summary>
      <div className="coach-mark-rows">
        {sorted.map((t) => {
          const cur = adherenceMark(t, ruleId);
          const pnl = Number(t.netPnl) || 0;
          return (
            <div className="coach-mark-row" key={t.id} data-trade={t.id}>
              <span className="mono coach-mark-date">{t.date}</span>
              <span className="coach-mark-what">
                {t.contract ?? '—'} ·{' '}
                <span className={`mono ${pnl >= 0 ? 'good' : 'bad'}`}>
                  {money(pnl, 0)}
                </span>
              </span>
              <span
                className="coach-seg"
                data-cur={cur === true ? 'true' : cur === false ? 'false' : 'skip'}
                role="group"
                aria-label={`Did you follow the rule on ${t.date}?`}
              >
                {(['true', 'false', 'skip'] as const).map((val) => {
                  const on = (val === 'true' && cur === true)
                    || (val === 'false' && cur === false)
                    || (val === 'skip' && cur === null);
                  return (
                    <button
                      key={val}
                      type="button"
                      className={`coach-seg-b ${val}${on ? ' on' : ''}`}
                      aria-pressed={on}
                      disabled={busy}
                      title={val === 'true' ? 'You followed the rule on this trade'
                        : val === 'false' ? 'You broke the rule on this trade'
                          : 'Not judged / the rule did not apply'}
                      onClick={() => onMark(t.id, ruleId, nextMark(cur, val))}
                    >{val === 'true' ? '✓' : val === 'false' ? '✗' : '—'}</button>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>
      {/* ⚠ STATED, not assumed. A trader who marks five trades and sees the
          verdict stay "Unmeasured" needs to know the threshold exists. */}
      <p className="muted coach-mark-note">
        A rule needs {num(ADHERENCE_MIN_MARKED, 0)} marked trades before adherence
        can be judged at all. An unmarked trade is no data — never a broken rule.
      </p>
    </details>
  );
}
